import { engineComponent, Logger, PreloaderInfo, StringUtils, Utils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { BezierPlugin } from "gsap";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { StageController } from "../Components/StageController";
import { AnimationStyle } from "../Components/Elements/Styles/AnimationStyle";
import { StyleManager } from "../Components/Managers/StyleManager";
import { Tweens } from "../Components/Effects/Tweens";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";

@engineComponent(ComponentTypes.DisplayObject)
export class RopeHeavenElement extends BaseElement {
    private _rope: PIXI.heaven.mesh.Rope;
    private _texture: PIXI.Texture;
    private _pointsCount: number;
    private _bezierPoints: Array<PIXI.heaven.mesh.RopePoint>;
    private _pointsData: Array<any>;
    private _pointsDataBuffer: Array<PIXI.heaven.mesh.RopePoint>;
    private _originalPoints: Array<PIXI.heaven.mesh.RopePoint> = [];
    private _blendMode: number;
    private _animationStyle: AnimationStyle;
    private _hasAnimationStyle: boolean;
    private _frame: number;
    private _tint: number;
    private _curviness: number;
    private _continue: boolean;
    private _isPlaying: boolean;
    private _debug: boolean;
    private _created: boolean;
    private _graphics: PIXI.Graphics;
    private _pointsAnimationAmplitude: number;
    private _pointsAnimationSpeed: number;
    private _pointsAnimationWavelength: number;
    private _canvasPadding: number = 1;
    private _angleCount: number;
    private _delayStartTimer: FrameTimerEventEmitter;
    private _delayStopTimer: FrameTimerEventEmitter;

    constructor() {
        super();

        this._rope = null;
        this._pointsCount = 100;
        this._bezierPoints = [];
        this._pointsData = [];
        this._pointsDataBuffer = [];
        this._blendMode = 0;
        this._animationStyle = null;
        this._hasAnimationStyle = false;
        this._frame = 0;
        this._tint = null;
        this._curviness = 1;
        this._continue = false;
        this._isPlaying = false;
        this._debug = false;
        this._created = false;
        this._pointsAnimationAmplitude = 0;
        this._pointsAnimationSpeed = 0;
        this._pointsAnimationWavelength = 0;
        this._angleCount = 0;

        this._delayStartTimer = CreateFrameTimer("delayStartTimer");
        this._delayStartTimer.on(FrameTimerEvents.Complete, this.start.bind(this));
        this._delayStopTimer = CreateFrameTimer("delayStopTimer");
        this._delayStopTimer.on(FrameTimerEvents.Complete, this.stop.bind(this));
    }

    public createByNode(node: Element): void {
        if (!this._created) {
            if (DEBUG) {
                if (
                    node.hasAttribute(RopeHeavenElementAttributes.DEBUG) &&
                    node.getAttribute(RopeHeavenElementAttributes.DEBUG) === "true"
                ) {
                    debugger;
                    this._debug = true;
                }
            }

            super.createByNode(node);

            if (node.hasAttribute(RopeHeavenElementAttributes.POINTS_COUNT)) {
                this._pointsCount = parseInt(node.getAttribute(RopeHeavenElementAttributes.POINTS_COUNT));
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.POINTS_ANIMATION_AMPLITUDE)) {
                this._pointsAnimationAmplitude = parseFloat(
                    node.getAttribute(RopeHeavenElementAttributes.POINTS_ANIMATION_AMPLITUDE),
                );
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.POINTS_ANIMATION_SPEED)) {
                this._pointsAnimationSpeed = parseFloat(
                    node.getAttribute(RopeHeavenElementAttributes.POINTS_ANIMATION_SPEED),
                );
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.POINTS_ANIMATION_WAVELENGTH)) {
                this._pointsAnimationWavelength = parseFloat(
                    node.getAttribute(RopeHeavenElementAttributes.POINTS_ANIMATION_WAVELENGTH),
                );
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.BLEND_MODE)) {
                this._blendMode = parseInt(node.getAttribute(RopeHeavenElementAttributes.BLEND_MODE));
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.CURVINESS)) {
                this._curviness = parseFloat(node.getAttribute(RopeHeavenElementAttributes.CURVINESS));
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.TINT)) {
                if (node.getAttribute(RopeHeavenElementAttributes.TINT) === "0") {
                    this._tint = null;
                } else {
                    this._tint = parseInt(node.getAttribute(RopeHeavenElementAttributes.TINT), 16);
                }
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.POINTS)) {
                let pointsData = this.formatPoints(node.getAttribute(RopeHeavenElementAttributes.POINTS));
                let bezierData = BezierPlugin.bezierThrough(pointsData);

                this._bezierPoints = this.getBezierStepPoints(bezierData, this._pointsCount);
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.IMAGE)) {
                let textureName = node.getAttribute(RopeHeavenElementAttributes.IMAGE);
                if (textureName.indexOf("ANI_") === 0) {
                    this._animationStyle = <AnimationStyle>StyleManager.Styles[textureName];
                    if (this._animationStyle != null) {
                        textureName = this._animationStyle.animation + "0000";
                        this._hasAnimationStyle = true;
                    } else {
                        this._hasAnimationStyle = false;
                    }
                }

                this._texture = StageController.getTextureByResourceId(textureName);
                if (this._texture != null) {
                    if (this._bezierPoints.length == 0) {
                        for (let i: number = 0; i < this._pointsCount; i++) {
                            this._bezierPoints.push(
                                new PIXI.heaven.mesh.RopePoint(
                                    Math.round(this._texture.width / (this._pointsCount - 1)) * i,
                                    0,
                                ),
                            );
                        }
                    }

                    for (let i: number = 0; i < this._bezierPoints.length; i++) {
                        this._originalPoints.push(
                            new PIXI.heaven.mesh.RopePoint(this._bezierPoints[i].x, this._bezierPoints[i].y),
                        );
                    }

                    this._rope = new PIXI.heaven.mesh.Rope(this._texture, this._bezierPoints);
                    this._rope.blendMode = this._blendMode;
                    this._rope.canvasPadding = this._canvasPadding;

                    if (this._tint != null) {
                        this._rope.tint = this._tint;
                    }

                    this.addChild(this._rope);

                    if (this._debug === true) {
                        this._graphics = new PIXI.Graphics();
                        if (this._graphics) {
                            this.addChild(this._graphics);
                        }
                    }
                }
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.CANVAS_PADDING)) {
                this._canvasPadding = parseInt(node.getAttribute(RopeHeavenElementAttributes.CANVAS_PADDING));
            }

            this._created = true;
        }
    }

    /**
     * Add base points to buffer.
     * @param {Array<any>} points
     */
    public addPoints(points: Array<PIXI.heaven.mesh.RopePoint>): void {
        this._pointsDataBuffer = this._pointsDataBuffer.concat(points);
    }

    /**
     * Get base points to buffer.
     */
    public getPoints(): Array<any> {
        return this._pointsData;
    }

    /**
     *
     * @param {Element} node
     */
    public updateByNode(node: Element): void {
        if (this._created) {
            if (DEBUG) {
                if (
                    node.hasAttribute(RopeHeavenElementAttributes.DEBUG) &&
                    node.getAttribute(RopeHeavenElementAttributes.DEBUG) === "true"
                ) {
                    debugger;
                    this._debug = true;
                }
            }

            super.updateByNode(node);

            if (node.hasAttribute(RopeHeavenElementAttributes.POINTS)) {
                let objArray: Array<Object> = this.formatPoints(node.getAttribute(RopeHeavenElementAttributes.POINTS));
                let XMLpointsData: Array<PIXI.heaven.mesh.RopePoint> = objArray as Array<PIXI.heaven.mesh.RopePoint>;
                this._pointsData = [];

                if (
                    node.hasAttribute(RopeHeavenElementAttributes.REVERSE_POINTS) &&
                    StringUtils.parseBoolean(node.getAttribute(RopeHeavenElementAttributes.REVERSE_POINTS))
                ) {
                    this._pointsData = XMLpointsData.concat(this._pointsDataBuffer).reverse();
                } else {
                    this._pointsData = this._pointsDataBuffer.concat(XMLpointsData);
                }
                this._pointsDataBuffer = [];
                this.updatePoints(this._pointsData);
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.GLOBAL_POINTS)) {
                this._pointsData = this.formatPoints(node.getAttribute(RopeHeavenElementAttributes.POINTS), true);
                this.updatePoints(this._pointsData);
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.TINT)) {
                if (this._rope != null) {
                    if (node.getAttribute(RopeHeavenElementAttributes.TINT) === "0") {
                        this._tint = null;
                    } else {
                        this._tint = parseInt(node.getAttribute(RopeHeavenElementAttributes.TINT), 16);
                    }
                    this._rope.tint = this._tint;
                }
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.IMAGE)) {
                let textureName = node.getAttribute(RopeHeavenElementAttributes.IMAGE);
                if (textureName.indexOf("ANI_") === 0) {
                    this._animationStyle = <AnimationStyle>StyleManager.Styles[textureName];
                    if (this._animationStyle != null) {
                        textureName = this._animationStyle.animation + "0000";
                        this._hasAnimationStyle = true;
                    } else {
                        this._hasAnimationStyle = false;
                    }
                }

                this._texture = StageController.getTextureByResourceId(textureName);
                if (this._texture != null) {
                    if (this._rope != null) {
                        this._rope.texture = this._texture;
                    }
                }
            }

            if (
                node.hasAttribute(RopeHeavenElementAttributes.CONTINUE) &&
                StringUtils.parseBoolean(node.getAttribute(RopeHeavenElementAttributes.CONTINUE))
            ) {
                this._continue = StringUtils.parseBoolean(node.getAttribute(RopeHeavenElementAttributes.CONTINUE));
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.START)) {
                //this.start();
                this._delayStartTimer.start(parseInt(node.getAttribute(RopeHeavenElementAttributes.START)));
            }

            if (node.hasAttribute(RopeHeavenElementAttributes.STOP)) {
                //this.stop();
                this._delayStopTimer.start(parseInt(node.getAttribute(RopeHeavenElementAttributes.STOP)));
            }
        }
    }

    /**
     *
     */
    private start(): void {
        if (this._animationStyle != null && this._hasAnimationStyle === true) {
            if (!this._continue || !this._isPlaying) {
                Tweens.removeTweens(this, "_frame");

                Tweens.customTween(this, {
                    tween: "_frame",
                    tweenValues: [0, this._animationStyle.endFrame],
                    tweenDuration: [60 / this._animationStyle.speed],
                    tweenEase: ["linear"],
                    tweenLoop: this._animationStyle.loop,
                    callbackType: "onUpdate",
                    callbackFunction: this.updateTexture,
                    callbackScope: this,
                    callbackParams: ["{self}"],
                });

                this._isPlaying = true;
            }
        } else {
            if (this._hasAnimationStyle === false) {
                if (!this._continue || !this._isPlaying) {
                    Tweens.removeTweens(this, "_frame");

                    Tweens.customTween(this, {
                        tween: "_frame",
                        tweenValues: [0, 59],
                        tweenDuration: [60],
                        tweenEase: ["linear"],
                        tweenLoop: true,
                        callbackType: "onUpdate",
                        callbackFunction: this.updateTexture,
                        callbackScope: this,
                        callbackParams: ["{self}"],
                    });

                    this._isPlaying = true;
                }
            }
        }
    }

    /**
     *
     */
    private stop(): void {
        // if (!this._continue) {
        Tweens.removeTweens(this, "_frame");
        this._isPlaying = false;
        // }
    }

    /**
     *
     * @param self
     */
    private updateTexture(self: any): void {
        if (this._animationStyle != null) {
            if (this._animationStyle.startFrame != null && this._animationStyle.endFrame != null) {
                if (Math.abs(this._animationStyle.startFrame - this._animationStyle.endFrame) > 0) {
                    let currentFrame: number = Math.round(this._frame);

                    if (currentFrame > self.previousValue || currentFrame == 0) {
                        this._rope.texture = StageController.getTextureByResourceId(
                            this._animationStyle.animation + this.zerosPad(currentFrame, 4),
                        );
                    }
                    self.previousValue = currentFrame;
                }
            }
        }

        // this._rope.refreshVertices()
        this.animateMeshPoints();

        if (this._debug === true) {
            this.renderPointsRope();
        }
    }

    private getTextureByResourceId(id: string): PIXI.Sprite {
        let texture: PIXI.Sprite = PIXI.utils.TextureCache[id];
        let resourcePath: string = "";

        if (Utils.isNullOrUndefined(texture)) {
            resourcePath = PreloaderInfo.instance.getResourcePathById(id);
            if (resourcePath) {
                texture = PIXI.utils.TextureCache[resourcePath];

                if (Utils.isNullOrUndefined(texture)) {
                    Logger.errorGroup(
                        "StageController",
                        `Could not find texture by id '${id}' (resourcePath: ${resourcePath})`,
                    );
                }
            } else {
                Logger.errorGroup("StageController", `Could not find resource path for id '${id}' !`);
            }
        }

        return texture;
    }

    /**
     *
     * @param num
     * @param size
     */
    private zerosPad(num: number, size: number): string {
        let s = num + "";
        while (s.length < size) s = "0" + s;
        return s;
    }

    private animateMeshPoints(): void {
        if (this._rope != null && this._pointsCount != null && this._originalPoints != null) {
            if (this._pointsAnimationSpeed > 0) {
                this._angleCount += this._pointsAnimationSpeed;

                if (this._angleCount >= 2 * Math.PI) {
                    this._angleCount = this._angleCount % (2 * Math.PI);
                }

                for (let i: number = 0; i < this._pointsCount; i++) {
                    this._rope.points[i].y =
                        this._originalPoints[i].y +
                        this._pointsAnimationAmplitude *
                            Math.sin(this._angleCount + i * this._pointsAnimationWavelength);
                    this._rope.vertices[i] =
                        this._originalPoints[i].y +
                        this._pointsAnimationAmplitude *
                            Math.sin(this._angleCount + i * this._pointsAnimationWavelength);
                }
            }
        }
    }

    /**
     *
     * @param {Array<any>} points
     */
    private updateMeshPoints(points: Array<any>): void {
        if (this._rope != null) {
            for (let i: number = 0; i < this._pointsCount; i++) {
                this._rope.points[i].x = points[i].x;
                this._rope.points[i].y = points[i].y;
            }
        }
    }

    /**
     *
     * @param {Array<any>} points
     */
    public updatePoints(points: Array<any>): void {
        let bezierData = BezierPlugin.bezierThrough(points, this._curviness);
        this._bezierPoints = this.getBezierStepPoints(bezierData, this._pointsCount);

        this.updateMeshPoints(this._bezierPoints);
    }

    public updateByArrayPIXIPoint(points: Array<PIXI.Point>): void {
        this.updatePoints(points);
    }

    /**
     *
     * @param {string} points
     * @param {boolean} useGlobal
     * @returns {Array<PIXI.Point>}
     */ /*
    private formatPoints(points: string, useGlobal: boolean = false): Array<PIXI.Point> {
        let result: Array<PIXI.Point> = [];
        let regex = /\[(.*?)\]/g;
        let data = points.match(regex);

        for (let i: number = 0; i < data.length; i++) {
            let pointArray = data[i].substring(1, data[i].length - 1).split(',');

            if (pointArray.length == 2) {
                let x = this.formatPoint(pointArray[0], "x", useGlobal);
                let y = this.formatPoint(pointArray[1], "y", useGlobal);

                result.push(new PIXI.Point(x, y));
            }
        }

        return result;
    }*/

    /**
     *
     * @param {string} value
     * @param {string} id
     * @param {boolean} useGlobal
     * @returns {number}
     */ /*
    private formatPoint(value: string, id: string, useGlobal: boolean = false): number {
        let result: number;
        if (value == "c") {
            result = this[id];
        } else {
            if (useGlobal) {
                result = this[id] + (parseInt(value) - this.getGlobalPosition()[id]);
            } else {
                result = parseInt(value);
            }
        }

        return result;
    }*/

    /**
     *
     * @param data
     * @param {number} length
     * @returns {Array<PIXI.Point>}
     */
    private getBezierStepPoints(data: any, length: number): Array<PIXI.heaven.mesh.RopePoint> {
        let segmentLength = length / data.x.length;
        let pointPerSegment: Array<number> = [];
        let bezierStepPoints: Array<PIXI.heaven.mesh.RopePoint> = [];

        if (data.x.length > 1) {
            segmentLength = Math.round(segmentLength);
            let sum = 0;
            for (let i: number = 0; i < data.x.length - 1; i++) {
                pointPerSegment.push(segmentLength);
                sum += segmentLength;
            }
            pointPerSegment.push(length - sum);
        } else {
            pointPerSegment = [segmentLength];
        }

        let time = 0;
        for (let i: number = 0; i < data.x.length; i++) {
            for (let j: number = 0; j < pointPerSegment[i]; j++) {
                if (i + 1 < data.x.length) {
                    time = j / pointPerSegment[i];
                } else {
                    time = j / (pointPerSegment[i] - 1);
                }
                bezierStepPoints.push(this.bezier(time, data.x[i], data.y[i]));
            }
        }

        return bezierStepPoints;
    }

    /**
     *
     * @param data
     * @param {number} length
     * @returns {Array<PIXI.Point>}
     */
    private getBezierStepRopePoints(data: any, length: number): Array<PIXI.heaven.mesh.RopePoint> {
        let segmentLength = length / data.x.length;
        let pointPerSegment: Array<number> = [];
        let bezierStepPoints: Array<PIXI.heaven.mesh.RopePoint> = [];

        if (data.x.length > 1) {
            segmentLength = Math.round(segmentLength);
            let sum = 0;
            for (let i: number = 0; i < data.x.length - 1; i++) {
                pointPerSegment.push(segmentLength);
                sum += segmentLength;
            }
            pointPerSegment.push(length - sum);
        } else {
            pointPerSegment = [segmentLength];
        }

        let time = 0;
        for (let i: number = 0; i < data.x.length; i++) {
            for (let j: number = 0; j < pointPerSegment[i]; j++) {
                if (i + 1 < data.x.length) {
                    time = j / pointPerSegment[i];
                } else {
                    time = j / (pointPerSegment[i] - 1);
                }
                bezierStepPoints.push(this.bezierRope(time, data.x[i], data.y[i]));
            }
        }

        return bezierStepPoints;
    }

    /**
     *
     * @param {number} t
     * @param {IBezierData} dataX
     * @param {IBezierData} dataY
     * @returns {PIXI.Point}
     */
    private bezier(t: number, dataX: IBezierData, dataY: IBezierData): PIXI.heaven.mesh.RopePoint {
        let cX = 3 * dataX.ba,
            bX = 3 * (dataX.c - dataX.b) - cX,
            aX = dataX.da - cX - bX;

        let cY = 3 * dataY.ba,
            bY = 3 * (dataY.c - dataY.b) - cY,
            aY = dataY.da - cY - bY;

        let x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + dataX.a;
        let y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + dataY.a;

        return new PIXI.heaven.mesh.RopePoint(x, y);
    }

    //******************************************************************************************************************
    //  FOR DEBUG, RENDER POINTS
    //******************************************************************************************************************

    private renderPointsRope(): void {
        if (this._graphics) {
            if (this._rope != null && this._rope.points.length > 0) {
                this._graphics.clear();

                this._graphics.lineStyle(2, 0xffc2c2);
                this._graphics.moveTo(this._rope.points[0].x, this._rope.points[0].y);

                for (let i = 1; i < this._rope.points.length; i++) {
                    this._graphics.lineTo(this._rope.points[i].x, this._rope.points[i].y);
                }

                for (let i = 0; i < this._rope.points.length; i++) {
                    this._graphics.beginFill(0xff0022);
                    this._graphics.drawCircle(this._rope.points[i].x, this._rope.points[i].y, 4);
                    this._graphics.endFill();
                }
            }
        }
    }

    /**
     *
     * @param {number} t
     * @param {IBezierData} dataX
     * @param {IBezierData} dataY
     * @returns {PIXI.Point}
     */
    private bezierRope(t: number, dataX: IBezierData, dataY: IBezierData): PIXI.heaven.mesh.RopePoint {
        let cX = 3 * dataX.ba,
            bX = 3 * (dataX.c - dataX.b) - cX,
            aX = dataX.da - cX - bX;

        let cY = 3 * dataY.ba,
            bY = 3 * (dataY.c - dataY.b) - cY,
            aY = dataY.da - cY - bY;

        let x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + dataX.a;
        let y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + dataY.a;

        return new PIXI.heaven.mesh.RopePoint(x, y);
    }

    public setTint(tintColor: number): void {
        if (this._tint === tintColor || isNaN(tintColor) || this._rope == null) {
            return;
        }

        this._tint = tintColor;
        this._rope.tint = this._tint;
    }

    /**
     *
     * @returns {RopeHeavenElement}
     */
    clone(): RopeHeavenElement {
        let element: RopeHeavenElement = new RopeHeavenElement();

        element.visible = this.visible;
        element.alpha = this.alpha;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;
        element._debug = this._debug;
        element._canvasPadding = this._canvasPadding;
        element._pointsCount = this._pointsCount;
        element._texture = this._texture;
        element._blendMode = this._blendMode;
        element._animationStyle = this._animationStyle;
        element._hasAnimationStyle = this._hasAnimationStyle;
        element._frame = this._frame;
        element._tint = this._tint;
        element._pointsAnimationAmplitude = this._pointsAnimationAmplitude;
        element._pointsAnimationSpeed = this._pointsAnimationSpeed;
        element._pointsAnimationWavelength = this._pointsAnimationWavelength;
        element._created = true;
        element._continue = this._continue;
        element._isPlaying = this._isPlaying;

        element._bezierPoints = [];
        for (let i = 0; i < this._bezierPoints.length; i++) {
            element._bezierPoints.push(
                new PIXI.heaven.mesh.RopePoint(this._bezierPoints[i].x, this._bezierPoints[i].y),
            );
        }

        element._originalPoints = [];
        for (let i = 0; i < this._originalPoints.length; i++) {
            element._originalPoints.push(
                new PIXI.heaven.mesh.RopePoint(this._originalPoints[i].x, this._originalPoints[i].y),
            );
        }

        if (element._texture != null) {
            element._rope = new PIXI.heaven.mesh.Rope(element._texture, element._bezierPoints);
        }

        if (element._rope != null) {
            element._rope.blendMode = element._blendMode;
            element.addChild(element._rope);
        }

        element.markAsCloned(); // must be called last before return!
        return element;
    }
}

export interface IBezierData {
    a: number;
    b: number;
    ba: number;
    c: number;
    ca: number;
    d: number;
    da: number;
}

const RopeHeavenElementAttributes = {
    /** Canvas padding for mesh (overlay of mesh polygons to avoid gaps)
     * @property CanvasPadding
     * @type number
     * @default 1
     */
    CANVAS_PADDING: "CanvasPadding",
    /**
     * Debug attribute must be parent only
     * */
    DEBUG: "Debug",
    /**
     * Blend mode of mesh object.
     * @property BlendMode
     * @default 0
     */
    BLEND_MODE: "BlendMode",
    /**
     * Bezier curviness.
     * @default 1
     */
    CURVINESS: "Curviness",
    /**
     * Start animation in random frame.
     * @default false
     */
    RANDOM_START_FRAME: "RandomStartFrame",
    /**
     * Start time of the first animation loop. Automatically set RandomStartFrame to false.
     * @default false
     */
    TIME: "Time",
    /**
     * Global points, pairs of X,Y coords for mesh.
     * @property Points
     * @default null
     */
    GLOBAL_POINTS: "GlobalPoints",
    /**
     * Reference to image from resources.
     * @property Image
     * @default 0
     */
    IMAGE: "Image",
    /**
     * Points, pairs of X,Y coords for mesh.
     * @property Points
     * @default null
     */
    POINTS: "Points",
    /**
     * Number of X,Y points for mesh. Higher make mesh detailed.
     * @property PointsCount
     * @default 100
     */
    POINTS_COUNT: "PointsCount",
    /**
     * Reverse collected input points.
     */
    REVERSE_POINTS: "ReversePoints",
    /**
     * Amplitude animation every points Y axis if 'PointsAnimationSpeed' > 0
     * * @property PointsAnimationAmplitude
     * @default 0
     */
    POINTS_ANIMATION_AMPLITUDE: "PointsAnimationAmplitude",
    /**
     * Speed animation every points Y axis if > 0
     * @property PointsAnimationSpeed
     * @default 0
     */
    POINTS_ANIMATION_SPEED: "PointsAnimationSpeed",
    /**
     * Wavelength animation every points Y axis if 'PointsAnimationSpeed' > 0
     * @property PointsAnimationWavelength
     * @default 0
     */
    POINTS_ANIMATION_WAVELENGTH: "PointsAnimationWavelength",
    /**
     * Start animation
     * @property Start
     * @default 0
     */
    START: "Start",
    /**
     * If true, animation will not be stopped and restarted, when START is fired
     * @property Continue
     * @default false
     */
    CONTINUE: "Continue",
    /**
     * Stop animation
     * @property Stop
     * @default 0
     */
    STOP: "Stop",
    /**
     * Tint of image. Hex color which colorize movie clip.
     * @property Tint
     * @default null
     */
    TINT: "Tint",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/RopeHeavenElement.ts
 **/